import { gql } from '@apollo/client'

const ITEM_FIELDS = gql`
  fragment ShoppingItemFields on ShoppingListItem {
    item_id
    list_id
    item_name
    quantity
    unit_of_measurement_id
    unit_of_measurement {
      unit_of_measurement_id
      unit_name
      unit_abbreviation
    }
    category
    is_checked
    is_staple
    quantity_edited
    similar_item_ids
    sort_order
    sources {
      source_id
      source_name
      recipe_id
      recipe_name
      quantity_string
      unit_label
    }
  }
`

const LIST_FIELDS = gql`
  fragment ShoppingListFields on ShoppingList {
    list_id
    list_name
    owner_name
    is_owner
    date_created
    date_updated
    items {
      ...ShoppingItemFields
    }
    recipes {
      recipe_id
      name
      slug
      item_count
    }
    collaborators {
      collaborator_id
      user_id
      display_name
      status_id
    }
  }
  ${ITEM_FIELDS}
`

export const MY_SHOPPING_LISTS = gql`
  query MyShoppingLists {
    myShoppingLists {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const SHOPPING_UNITS_QUERY = gql`
  query ShoppingUnits {
    getUnitOfMesurement {
      unit_of_measurement_id
      unit_name
      unit_abbreviation
    }
  }
`

export const CREATE_SHOPPING_LIST = gql`
  mutation CreateShoppingList($name: String!) {
    createShoppingList(name: $name) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const RENAME_SHOPPING_LIST = gql`
  mutation RenameShoppingList($listId: Int!, $name: String!) {
    renameShoppingList(list_id: $listId, name: $name) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const DELETE_SHOPPING_LIST = gql`
  mutation DeleteShoppingList($listId: Int!) {
    deleteShoppingList(list_id: $listId)
  }
`

export const ADD_SHOPPING_ITEM = gql`
  mutation AddShoppingItem($input: ShoppingItemInput!) {
    addShoppingItem(input: $input) {
      ...ShoppingItemFields
    }
  }
  ${ITEM_FIELDS}
`

export const UPDATE_SHOPPING_ITEM = gql`
  mutation UpdateShoppingItem($itemId: Int!, $input: ShoppingItemUpdateInput!) {
    updateShoppingItem(item_id: $itemId, input: $input) {
      ...ShoppingItemFields
    }
  }
  ${ITEM_FIELDS}
`

export const REMOVE_SHOPPING_ITEM = gql`
  mutation RemoveShoppingItem($itemId: Int!) {
    removeShoppingItem(item_id: $itemId)
  }
`

export const CLEAR_SHOPPING_LIST_MARKS = gql`
  mutation ClearShoppingListMarks($listId: Int!) {
    clearShoppingListMarks(list_id: $listId) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const BULK_UPDATE_SHOPPING_ITEMS = gql`
  mutation BulkUpdateShoppingItems($input: ShoppingBulkUpdateInput!) {
    bulkUpdateShoppingItems(input: $input) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const ADD_RECIPE_TO_SHOPPING_LIST = gql`
  mutation AddRecipeToShoppingList(
    $listId: Int!
    $recipeId: Int!
    $redirects: [RecipeRedirectInput!]
  ) {
    addRecipeToShoppingList(list_id: $listId, recipe_id: $recipeId, redirects: $redirects) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const CREATE_SHOPPING_LIST_WITH_RECIPE = gql`
  mutation CreateShoppingListWithRecipe(
    $name: String!
    $recipeId: Int!
    $redirects: [RecipeRedirectInput!]
  ) {
    createShoppingListWithRecipe(name: $name, recipe_id: $recipeId, redirects: $redirects) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const REMOVE_RECIPE_FROM_SHOPPING_LIST = gql`
  mutation RemoveRecipeFromShoppingList($listId: Int!, $recipeId: Int!) {
    removeRecipeFromShoppingList(list_id: $listId, recipe_id: $recipeId) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const REORDER_SHOPPING_ITEMS = gql`
  mutation ReorderShoppingItems($input: ShoppingReorderInput!) {
    reorderShoppingItems(input: $input) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const MERGE_SHOPPING_ITEMS = gql`
  mutation MergeShoppingItems($listId: Int!, $itemIds: [Int!]!) {
    mergeShoppingItems(list_id: $listId, item_ids: $itemIds) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const SPLIT_SHOPPING_ITEM = gql`
  mutation SplitShoppingItem($itemId: Int!) {
    splitShoppingItem(item_id: $itemId) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const DISMISS_SHOPPING_SUGGESTION = gql`
  mutation DismissShoppingSuggestion($itemId: Int!, $otherItemId: Int!) {
    dismissShoppingSuggestion(item_id: $itemId, other_item_id: $otherItemId) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const SHOPPING_RECIPE_OVERLAPS = gql`
  query ShoppingRecipeOverlaps($recipeId: Int!, $excludeListId: Int) {
    shoppingRecipeOverlaps(recipe_id: $recipeId, exclude_list_id: $excludeListId) {
      recipe_ingredient_id
      ingredient_name
      item_id
      item_name
      item_quantity
      list_id
      list_name
      exact
    }
  }
`

export const CREATE_SHOPPING_LIST_INVITE = gql`
  mutation CreateShoppingListInvite($listId: Int!) {
    createShoppingListInvite(list_id: $listId)
  }
`

export const RESET_SHOPPING_LIST_INVITE = gql`
  mutation ResetShoppingListInvite($listId: Int!) {
    resetShoppingListInvite(list_id: $listId)
  }
`

export const ACCEPT_SHOPPING_LIST_INVITE = gql`
  mutation AcceptShoppingListInvite($token: String!) {
    acceptShoppingListInvite(token: $token) {
      ...ShoppingListFields
    }
  }
  ${LIST_FIELDS}
`

export const REMOVE_SHOPPING_LIST_MEMBER = gql`
  mutation RemoveShoppingListMember($listId: Int!, $collaboratorId: Int!) {
    removeShoppingListMember(list_id: $listId, collaborator_id: $collaboratorId)
  }
`

// Public: works without signing in, so the invite page can show who invited you.
export const SHOPPING_LIST_INVITE_PREVIEW = gql`
  query ShoppingListInvitePreview($token: String!) {
    shoppingListInvitePreview(token: $token) {
      list_name
      owner_name
      member_count
    }
  }
`
